import React, { useState, useEffect, useRef } from 'react';
import { API } from '../config';

export default function ClassBoard({ currentUser }) {
  const [boards, setBoards] = useState([]);
  const [activeBoard, setActiveBoard] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMsg, setNewMsg] = useState('');
  const [sending, setSending] = useState(false);
  const [aiMode, setAiMode] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const msgEndRef = useRef(null);

  useEffect(() => {
    if (!currentUser) return;
    fetch(`${API.apiBase}/api/boards?userId=${currentUser.id}`)
      .then(r => r.json())
      .then(data => {
        setBoards(data);
        if (data.length > 0 && !activeBoard) setActiveBoard(data[0].id);
      })
      .catch(() => {});
  }, [currentUser]);

  useEffect(() => {
    if (!activeBoard) return;
    fetch(`${API.apiBase}/api/boards/${activeBoard}/messages`)
      .then(r => r.json())
      .then(setMessages)
      .catch(() => {});
  }, [activeBoard]);

  useEffect(() => {
    msgEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  async function handleSend(e) {
    e.preventDefault();
    if (!newMsg.trim() || sending || aiLoading) return;

    if (aiMode) {
      setAiLoading(true);
      const userMsg = { id: Date.now(), userId: currentUser.id, userName: currentUser.name, text: newMsg.trim(), createdAt: new Date().toISOString() };
      setMessages(prev => [...prev, userMsg]);
      const q = newMsg.trim();
      setNewMsg('');
      try {
        const r = await fetch(`${API.apiBase}/api/boards/${activeBoard}/ai-chat`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ userId: currentUser.id, text: q })
        });
        if (r.ok) {
          const data = await r.json();
          setMessages(prev => [...prev, { id: Date.now() + 1, userId: 0, userName: '🤖 AI管理员', text: data.text, createdAt: new Date().toISOString(), ai: true }]);
        }
      } catch {}
      setAiLoading(false);
      return;
    }

    setSending(true);
    try {
      const r = await fetch(`${API.apiBase}/api/boards/${activeBoard}/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: currentUser.id, text: newMsg.trim() })
      });
      if (r.ok) {
        const msg = await r.json();
        setMessages(prev => [...prev, msg]);
        setNewMsg('');
      }
    } catch {}
    setSending(false);
  }

  const board = boards.find(b => b.id === activeBoard);

  return (
    <div>
      {boards.length === 0 && (
        <div style={{ padding: 20, textAlign: 'center', color: '#999' }}>
          暂无班级留言板，完善个人信息中的学校和班级后自动创建
        </div>
      )}

      {boards.length > 0 && (
        <div style={{ marginBottom: 10, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {boards.map(b => (
            <button key={b.id} onClick={() => setActiveBoard(b.id)}
              style={{
                padding: '6px 14px', border: 'none', borderRadius: 14,
                background: activeBoard === b.id ? '#7c4dff' : '#f0f0f0',
                color: activeBoard === b.id ? '#fff' : '#333',
                fontSize: 13, cursor: 'pointer', fontWeight: 500
              }}>
              {b.schoolName} · {b.className}
            </button>
          ))}
        </div>
      )}

      {board && (
        <div style={{ fontSize: 12, color: '#999', marginBottom: 8 }}>
          {board.memberCount} 位同学 · {board.messageCount} 条留言
        </div>
      )}

      {activeBoard && (
        <div style={{ border: '1px solid #eee', borderRadius: 8, background: '#fff', minHeight: 300, display: 'flex', flexDirection: 'column' }}>
          <div style={{ flex: 1, padding: 10, maxHeight: 400, overflowY: 'auto' }}>
            {messages.length === 0 && (
              <div style={{ textAlign: 'center', color: '#ccc', padding: 40, fontSize: 14 }}>
                暂无留言，来打个招呼吧
              </div>
            )}
            {messages.map(m => (
              <div key={m.id} style={{ marginBottom: 10, display: 'flex', flexDirection: m.userId === currentUser?.id ? 'row-reverse' : 'row', alignItems: 'flex-end', gap: 6 }}>
                <div style={{
                  maxWidth: '75%',
                  padding: '8px 12px',
                  borderRadius: m.userId === currentUser?.id ? '12px 12px 2px 12px' : '12px 12px 12px 2px',
                  background: m.ai ? '#fff3e0' : (m.userId === currentUser?.id ? '#7c4dff' : '#f5f5f5'),
                  color: m.ai ? '#e65100' : (m.userId === currentUser?.id ? '#fff' : '#333'),
                  fontSize: 14,
                  lineHeight: 1.5,
                  border: m.ai ? '1px solid #ffe0b2' : 'none',
                }}>
                  <div style={{ fontSize: 11, fontWeight: 600, marginBottom: 2, opacity: 0.7 }}>
                    {m.userName}
                    {m.ai && <span style={{ fontSize: 9, marginLeft: 4, color: '#ff9800' }}>AI</span>}
                  </div>
                  <div style={{ whiteSpace: 'pre-wrap' }}>{m.text}</div>
                  <div style={{ fontSize: 10, textAlign: 'right', marginTop: 4, opacity: 0.5 }}>
                    {new Date(m.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </div>
                </div>
              </div>
            ))}
            <div ref={msgEndRef} />
          </div>

          <div style={{ display: 'flex', gap: 6, padding: '4px 8px 0', borderTop: '1px solid #eee' }}>
            <button onClick={() => setAiMode(false)}
              style={{ padding: '4px 10px', border: 'none', borderRadius: 12, fontSize: 11, cursor: 'pointer',
                background: !aiMode ? '#7c4dff' : '#f0f0f0', color: !aiMode ? '#fff' : '#666' }}>
              💬 群聊
            </button>
            <button onClick={() => setAiMode(true)}
              style={{ padding: '4px 10px', border: 'none', borderRadius: 12, fontSize: 11, cursor: 'pointer',
                background: aiMode ? '#ff9800' : '#f0f0f0', color: aiMode ? '#fff' : '#666' }}>
              🤖 问AI
            </button>
            {aiMode && <span style={{ fontSize: 11, color: '#ff9800', alignSelf: 'center' }}>AI 管理员帮你解答问题</span>}
          </div>
          <form onSubmit={handleSend} style={{ display: 'flex', gap: 6, padding: 8 }}>
            <input
              value={newMsg}
              onChange={e => setNewMsg(e.target.value)}
              placeholder={aiMode ? "问AI管理员..." : "说点什么..."}
              style={{ flex: 1, padding: '8px 12px', border: '1px solid #ddd', borderRadius: 20, fontSize: 14, outline: 'none' }}
            />
            <button type="submit" disabled={sending || aiLoading || !newMsg.trim()}
              style={{ padding: '8px 16px', border: 'none', borderRadius: 20, background: aiMode ? '#ff9800' : '#7c4dff', color: '#fff', fontSize: 14, cursor: 'pointer', opacity: (sending || aiLoading || !newMsg.trim()) ? 0.5 : 1 }}>
              {aiLoading ? '思考中...' : (aiMode ? '提问' : '发送')}
            </button>
          </form>
        </div>
      )}
    </div>
  );
}